import React from 'react';
import { StepHeader } from '../ui/StepHeader.jsx';
import { StepIntro } from '../ui/StepIntro.jsx';
import { IS_DEV } from '../utils/env.js';
import { centsToReais } from '../utils/masks.js';
import { parseDonationAmount } from '../utils/donationAmount.js';
import { FREQUENCY_SUFFIX } from '../utils/frequencies.js';
import { getVocabulary } from '../utils/vocabulary.js';
import { FrequencySelector } from '../ui/FrequencySelector.jsx';

/**
 * Step 1 — frequency choice, preset/custom amount.
 *
 * The frequency control itself is FrequencySelector, shared with the picker's
 * cart summary.
 */
export function AmountStep({ config, formData, onChange, onNext, onClose, isInline, isPreview }) {
  const { Frequencies } = config.Behaviour;
  const { Visual } = config;
  const vocab = getVocabulary(config);

  const selectedFrequency = Frequencies.find(f => f.Id === formData.frequencyId) || Frequencies[0];
  const isRecurringSelected = Boolean(selectedFrequency && selectedFrequency.Id !== 'OneTime');
  const isCustomAmount = formData.isCustomAmount === true;

  function handleFrequencyChange(frequencyId) {
    const freq = Frequencies.find(f => f.Id === frequencyId);
    if (!freq) return;
    onChange({
      frequencyId,
      amount: freq.SuggestedAmounts[0],
      customAmount: '',
      isCustomAmount: false,
      paymentMethod: freq.PaymentMethods[0] || null,
      paymentDetails: {}
    });
  }

  function handlePresetAmount(amount) {
    onChange({ amount, customAmount: '', isCustomAmount: false });
  }

  function handleCustomAmountChange(value) {
    const { text, cents } = parseDonationAmount(value);
    onChange({
      customAmount: text,
      isCustomAmount: text !== '',
      amount: cents
    });
  }

  function handleNext() {
    if (!isPreview && !canProceed) return;
    onNext();
  }

  const currentAmount = formData.amount;
  const amountText = centsToReais(currentAmount, {
    minimumFractionDigits: currentAmount % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  });

  const minAmount = selectedFrequency?.MinAmount || 0;

  const canProceed = selectedFrequency
    ? currentAmount >= selectedFrequency.MinAmount
    : false;

  const hasCustomValue = isCustomAmount && String(formData.customAmount || '').trim() !== '';

  const isBelowMinimum = hasCustomValue && currentAmount < minAmount;

  const freqSuffix = FREQUENCY_SUFFIX[selectedFrequency?.Id] || '';

  function fillFake() {
    const freq = Frequencies[Math.floor(Math.random() * Frequencies.length)];
    if (!freq) return;
    const amounts = freq.SuggestedAmounts || [];
    const amount = amounts.length ? amounts[Math.floor(Math.random() * amounts.length)] : (freq.MinAmount || 5000);
    onChange({
      frequencyId: freq.Id,
      amount,
      customAmount: '',
      isCustomAmount: false,
      paymentMethod: freq.PaymentMethods?.[0] || null,
      paymentDetails: {}
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <StepHeader
        onClose={onClose}
        showClose={!isInline}
        onDevFill={IS_DEV && !isPreview ? fillFake : undefined}
      />

      <div className="tdw-step-body">
        <StepIntro
          title={Visual?.FormTitle || `Quanto você quer ${vocab.verb}?`}
          subtitle={Visual?.FormSubtitle || null}
        />

        <FrequencySelector
          config={config}
          frequencyId={formData.frequencyId}
          onChange={handleFrequencyChange}
        />

        {/* Preset amounts */}
        {selectedFrequency && (
          <>
            <div className="tdw-amounts">
              {selectedFrequency.SuggestedAmounts.map(amount => (
                <button
                  key={amount}
                  type="button"
                  className={`tdw-amount-btn${!isCustomAmount && formData.amount === amount ? ' selected' : ''}`}
                  onClick={() => handlePresetAmount(amount)}
                >
                  <span className="tdw-money">R$ {centsToReais(amount)}</span>
                </button>
              ))}
            </div>

            {/* Custom amount — always visible */}
            {selectedFrequency.AllowCustomAmount && (
              <div className={`tdw-custom-amount-wrap${hasCustomValue ? ' selected' : ''}`}>
                <span className="tdw-custom-amount-prefix">R$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="tdw-custom-amount-input"
                  placeholder="Outro valor"
                  value={formData.customAmount || ''}
                  onChange={e => handleCustomAmountChange(e.target.value)}
                />
              </div>
            )}

            {isBelowMinimum && (
              <p className="tdw-min-amount-error">
                O valor mínimo é R$ {centsToReais(minAmount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            )}
          </>
        )}

        {isRecurringSelected && (
          <div className="tdw-recurring-note">
            Você pode alterar ou cancelar {vocab.your} {vocab.term} recorrente quando quiser.
          </div>
        )}

        <div style={{ flex: 1 }} />

        <button
          type="button"
          className="tdw-btn-cta"
          onClick={handleNext}
          disabled={!isPreview && !canProceed}
        >
          {canProceed
            ? <>{vocab.verbCapitalized} <span className="tdw-money">R$ {amountText}</span>{freqSuffix}</>
            : vocab.verbCapitalized}
        </button>
      </div>
    </div>
  );
}
